import { useCallback, useEffect, useRef, useState } from 'react';

import {
  ProposalCheckPatch,
  ProposalCheckResponse,
  getProposalCheck,
  updateProposalCheck,
} from 'services/actions/ai_management_proposal_check';

interface Options {
  trackSave: <T>(request: Promise<T>) => Promise<T>;
}

interface UndoToast {
  regra: string;
  message: string;
}

const UNDO_TIMEOUT = 7000;

// O interceptor global do Api resolve 4xx sem rejeitar: o erro vem pelo status e pelo `error`.
const errorOf = (res: { data: unknown; status?: number }, fallback: string): string | null => {
  const status = res?.status ?? 200;
  const body = res?.data as { error?: string; erro?: string } | undefined;
  if (status >= 400 || body?.error || body?.erro) return body?.error || body?.erro || fallback;
  return null;
};

const isCheck = (data: unknown): data is ProposalCheckResponse =>
  Boolean(data && typeof data === 'object' && 'itens' in data && 'pode_gerar' in data);

export function useProposalCheck(biddingId: number | null, { trackSave }: Options) {
  const [check, setCheck] = useState<ProposalCheckResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [undoToast, setUndoToast] = useState<UndoToast | null>(null);

  const biddingRef = useRef(biddingId);
  biddingRef.current = biddingId;
  const mounted = useRef(true);
  const undoTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      mounted.current = false;
      if (undoTimer.current) clearTimeout(undoTimer.current);
    },
    [],
  );

  const load = useCallback(() => {
    if (!biddingId) return;
    const id = biddingId;
    setLoading(true);
    setLoadError(false);
    getProposalCheck(id)
      .then((res) => {
        if (!mounted.current || biddingRef.current !== id) return;
        if (errorOf(res, 'erro') || !isCheck(res.data)) {
          setLoadError(true);
          return;
        }
        setCheck(res.data);
      })
      .catch(() => {
        if (mounted.current && biddingRef.current === id) setLoadError(true);
      })
      .finally(() => {
        if (mounted.current && biddingRef.current === id) setLoading(false);
      });
  }, [biddingId]);

  useEffect(() => {
    setCheck(null);
    setErrors({});
    setUndoToast(null);
    load();
  }, [load]);

  const dismissUndo = useCallback(() => {
    if (undoTimer.current) clearTimeout(undoTimer.current);
    setUndoToast(null);
  }, []);

  const write = useCallback(
    (regra: string, patch: ProposalCheckPatch): Promise<boolean> => {
      if (!biddingId) return Promise.resolve(false);
      const id = biddingId;
      setBusy(regra);
      setErrors((prev) => ({ ...prev, [regra]: undefined }));
      return trackSave(updateProposalCheck(id, regra, patch))
        .then((res) => {
          if (!mounted.current || biddingRef.current !== id) return false;
          const error = errorOf(res, 'Não foi possível salvar.');
          if (error || !isCheck(res.data)) {
            setErrors((prev) => ({ ...prev, [regra]: error ?? 'Não foi possível salvar.' }));
            return false;
          }
          setCheck(res.data);
          return true;
        })
        .catch(() => {
          if (mounted.current && biddingRef.current === id) {
            setErrors((prev) => ({ ...prev, [regra]: 'Não foi possível salvar.' }));
          }
          return false;
        })
        .finally(() => {
          if (mounted.current && biddingRef.current === id) setBusy(null);
        });
    },
    [biddingId, trackSave],
  );

  const markAware = useCallback(
    (regra: string) =>
      write(regra, { ciente: true }).then((ok) => {
        if (!ok || !mounted.current) return;
        if (undoTimer.current) clearTimeout(undoTimer.current);
        setUndoToast({ regra, message: 'Regra marcada como ciente.' });
        undoTimer.current = setTimeout(() => setUndoToast(null), UNDO_TIMEOUT);
      }),
    [write],
  );

  const undoAware = useCallback(() => {
    if (!undoToast) return;
    const { regra } = undoToast;
    dismissUndo();
    write(regra, { ciente: false });
  }, [undoToast, dismissUndo, write]);

  const markChecked = useCallback((regra: string) => write(regra, { conferida: true }), [write]);

  return {
    check,
    loading,
    loadError,
    busy,
    errors,
    undoToast,
    reload: load,
    markAware,
    undoAware,
    markChecked,
    dismissUndo,
  };
}
